export interface ApiFieldError {
  location: Array<string | number>;
  message: string;
  code?: string;
}

export class ApiError extends Error {
  readonly statusCode: number;
  readonly endpoint: string;
  readonly response?: unknown;
  readonly code: string;
  readonly requestId?: string;
  readonly details: ApiFieldError[];

  constructor(
    message: string,
    statusCode: number,
    endpoint: string,
    response?: unknown,
    code = 'HTTP_ERROR',
    requestId?: string,
    details: ApiFieldError[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.endpoint = endpoint;
    this.response = response;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}

export interface ApiRequestOptions extends RequestInit {
  timeoutMs?: number;
  retries?: number;
}

export const resolveApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    if (window.location.port === '3001') return 'http://localhost:8001/api/v1';
    return '/api/v1';
  }
  return 'http://localhost:8000/api/v1';
};

export const API_BASE_URL = resolveApiBaseUrl();

const RETRYABLE_STATUS_CODES = new Set([502, 503, 504]);

const isIdempotentRequest = (method?: string): boolean =>
  ['GET', 'HEAD', 'OPTIONS'].includes((method || 'GET').toUpperCase());

const isTransientError = (error: unknown): boolean =>
  (error instanceof ApiError && (
    RETRYABLE_STATUS_CODES.has(error.statusCode) || error.code === 'TIMEOUT'
  )) || error instanceof TypeError;

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const getStoredToken = (): string | null => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem('token');
  } catch {
    return null;
  }
};

const createRequestId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const normalizeFieldErrors = (value: unknown): ApiFieldError[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item): ApiFieldError[] => {
    if (!item || typeof item !== 'object') return [];
    const record = item as Record<string, unknown>;
    const location = Array.isArray(record.location)
      ? record.location
      : Array.isArray(record.loc) ? record.loc : [];
    const message = typeof record.message === 'string'
      ? record.message
      : typeof record.msg === 'string' ? record.msg : 'Invalid value';
    const code = typeof record.code === 'string'
      ? record.code
      : typeof record.type === 'string' ? record.type : undefined;
    return [{ location: location.filter((part): part is string | number =>
      typeof part === 'string' || typeof part === 'number'), message, code }];
  });
};

const errorFromResponse = async (response: Response, endpoint: string): Promise<ApiError> => {
  const bodyText = await response.text();
  let body: Record<string, unknown> = {};
  try {
    const parsed: unknown = JSON.parse(bodyText);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      body = parsed as Record<string, unknown>;
    }
  } catch {
    // Some proxies return plain text or HTML while the API is restarting.
  }

  const detail = body.detail;
  const detailRecord = detail && typeof detail === 'object' && !Array.isArray(detail)
    ? detail as Record<string, unknown>
    : undefined;
  const candidates = [body.message, detailRecord?.message, detail, body.error];
  const message = candidates.find((candidate): candidate is string =>
    typeof candidate === 'string' && candidate.length > 0,
  ) || (response.status >= 502
    ? `System unavailable (${response.status}); the service may be restarting.`
    : `API request failed: ${response.status} ${response.statusText}`);
  const details = normalizeFieldErrors(body.details ?? (Array.isArray(detail) ? detail : []));
  const code = typeof body.code === 'string'
    ? body.code
    : response.status === 422 ? 'VALIDATION_ERROR' : `HTTP_${response.status}`;
  const requestId = typeof body.request_id === 'string'
    ? body.request_id
    : response.headers.get('X-Request-ID') || undefined;

  return new ApiError(message, response.status, endpoint, bodyText || body, code, requestId, details);
};

const handleUnauthorized = (error: ApiError): void => {
  if (error.statusCode !== 401 || error.endpoint.includes('/auth/token')) return;
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem('token');
  } catch {
    // Storage can be disabled by browser privacy settings.
  }
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

const requestResponse = async (
  endpoint: string,
  options: ApiRequestOptions = {},
): Promise<Response> => {
  const { timeoutMs, signal, retries, ...requestOptions } = options;
  const headers = new Headers(requestOptions.headers || {});
  const token = getStoredToken();
  if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
  if (!headers.has('X-Request-ID')) headers.set('X-Request-ID', createRequestId());
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');

  const maxAttempts = isIdempotentRequest(requestOptions.method)
    ? Math.max(1, retries ?? 3)
    : 1;
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | undefined;
    let abortListener: (() => void) | undefined;

    if (timeoutMs && timeoutMs > 0) {
        controller = new AbortController();
        timeoutId = setTimeout(() => controller?.abort(), timeoutMs);
        if (signal) {
          if (signal.aborted) {
            controller.abort();
          } else {
            abortListener = () => controller?.abort();
            signal.addEventListener('abort', abortListener, { once: true });
          }
        }
    }

    try {
      const response = await fetch(endpoint, {
        ...requestOptions,
        headers,
        signal: controller?.signal ?? signal,
      });
      if (!response.ok) throw await errorFromResponse(response, endpoint);
      return response;
    } catch (error) {
      let finalError = error;
      if (error instanceof DOMException && error.name === 'AbortError'
        && controller?.signal.aborted && !signal?.aborted) {
        finalError = new ApiError(
          `Request timed out after ${timeoutMs}ms`, 0, endpoint, undefined, 'TIMEOUT',
        );
      }
      lastError = finalError;

      if (attempt >= maxAttempts || !isTransientError(finalError) || signal?.aborted) {
        if (finalError instanceof ApiError) handleUnauthorized(finalError);
        throw finalError;
      }
      await delay(250 * attempt);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      if (signal && abortListener) signal.removeEventListener('abort', abortListener);
    }
  }

  throw lastError;
};

export const apiRequest = async <T>(
  endpoint: string,
  options: ApiRequestOptions = {},
): Promise<T> => {
  const response = await requestResponse(endpoint, options);
  if (response.status === 204) return undefined as T;
  try {
    return await response.json() as T;
  } catch {
    throw new ApiError('The server returned an invalid JSON response.', response.status, endpoint, undefined, 'INVALID_RESPONSE');
  }
};

export const apiRequestBlob = async (
  endpoint: string,
  options: ApiRequestOptions = {},
): Promise<Blob> => (await requestResponse(endpoint, options)).blob();
